import { useState, useCallback, useEffect } from 'react';
import html2canvas from 'html2canvas';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, Typography, alpha, Alert,
  Box, IconButton, Tooltip, CircularProgress, Chip,
} from '@mui/material';
import BugReportIcon      from '@mui/icons-material/BugReport';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import CameraAltIcon      from '@mui/icons-material/CameraAlt';
import VideocamIcon       from '@mui/icons-material/Videocam';
import CloseIcon          from '@mui/icons-material/Close';
import ImageIcon          from '@mui/icons-material/Image';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';
import {
  useUploadBugAttachmentMutation,
  useCreateBugReportMutation,
  type BugAttachment,
} from '../../../store/api/platformApi';
import { AnnotationCanvas }  from './AnnotationCanvas';
import { useRecording }      from '../context/RecordingContext';

interface Props {
  open:                boolean;
  onClose:             () => void;
  pendingAttachment?:  BugAttachment | null;
  attachmentUploading?: boolean;
  attachmentError?:    boolean;
  /** Modo con el que abre el diálogo (lo elige el slide del panel de soporte). */
  initialKind?:        'bug' | 'suggestion';
  /** Renderiza el formulario SIN diálogo, para incrustarlo en el panel de Gustavo. */
  inline?:             boolean;
}

type View = 'form' | 'annotate';

// Áreas del CRM para clasificar una sugerencia (filtro de temas).
const SUGGESTION_TOPICS = ['Clientes', 'Propiedades', 'Gustavo IA', 'Finanzas', 'Calendario', 'Reportes', 'Portal / Web', 'Otro'] as const;

import { useSinReporteDeBugs } from '../hooks/useSinReporteDeBugs';

export function ReportBugDialog({ open, onClose, pendingAttachment, attachmentUploading, attachmentError, initialKind = 'bug', inline = false }: Props) {
  const golden = P.brand.golden;
  const recording = useRecording();
  // En Imperium el formulario queda solo para sugerencias (ver useSinReporteDeBugs).
  const soloSugerencia = useSinReporteDeBugs();
  if (soloSugerencia) initialKind = 'suggestion';

  const [view,        setView]        = useState<View>('form');
  const [kind,        setKind]        = useState<'bug' | 'suggestion'>(initialKind);
  const [topics,      setTopics]      = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [done,        setDone]        = useState(false);
  const [attachments, setAttachments] = useState<BugAttachment[]>([]);
  const [captureUrl,  setCaptureUrl]  = useState<string | null>(null);
  const [captureError, setCaptureError] = useState(false);
  const [annotated,   setAnnotated]   = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<'screenshot' | 'video' | null>(null);

  const [uploadAttachment] = useUploadBugAttachmentMutation();
  const [createReport, { isLoading: submitting, error, reset }] = useCreateBugReportMutation();

  // Al abrir, arranca en el modo elegido por el slide del panel (bug / suggestion).
  useEffect(() => {
    if (open) setKind(initialKind);
  }, [open, initialKind]);

  const handleClose = () => {
    if (submitting || uploadingId || attachmentUploading) return;
    setKind(initialKind);
    setTopics([]);
    setDescription('');
    setDone(false);
    setAttachments([]);
    setCaptureUrl(null);
    setAnnotated(null);
    setView('form');
    reset();
    onClose();
  };

  // ── Screenshot capture ─────────────────────────────────────────
  const handleCapture = async () => {
    // Hide dialog briefly, capture DOM, re-show
    setCaptureError(false);
    const dialogEl = document.querySelector('[data-bug-dialog]') as HTMLElement | null;
    if (dialogEl) dialogEl.style.visibility = 'hidden';
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true, scale: 1, logging: false, imageTimeout: 3000,
        // html2canvas tira InvalidStateError en createPattern cuando un elemento con
        // background-image se rinde a un canvas 0x0 (ej. avatares/banners sin dimensión
        // en el momento de capturar). Salteamos esos elementos + iframes para que la
        // captura no falle en páginas como /customers.
        ignoreElements: (el) => {
          if (el.tagName === 'IFRAME') return true;
          const r = el.getBoundingClientRect();
          if (r.width === 0 || r.height === 0) {
            const bg = getComputedStyle(el).backgroundImage;
            if (bg && bg !== 'none') return true;
          }
          return false;
        },
      });
      const url = canvas.toDataURL('image/png');
      setCaptureUrl(url);
      setAnnotated(url);
      setView('annotate');
    } catch {
      // Falla típica: imágenes cross-origin (fotos de propiedades) "ensucian" el canvas
      // y toDataURL tira → antes quedaba mudo. Avisamos y ofrecemos subir imagen manual.
      setCaptureError(true);
    } finally {
      if (dialogEl) dialogEl.style.visibility = '';
    }
  };

  // Fallback: subir una imagen del disco (si la captura automática falla). (bug Diego)
  const handleManualImage = async (file?: File | null) => {
    if (!file) return;
    setCaptureError(false);
    setUploadingId('screenshot');
    try {
      const fd = new FormData();
      fd.append('file', file, file.name || 'captura.png');
      const att = await uploadAttachment(fd).unwrap();
      setAttachments((prev) => [...prev.filter((a) => a.type !== 'screenshot'), att]);
    } catch { setCaptureError(true); }
    finally { setUploadingId(null); }
  };

  // ── Annotation confirmed ───────────────────────────────────────
  const handleAnnotationConfirm = useCallback(async (dataUrl: string) => {
    setView('form');
    setUploadingId('screenshot');
    try {
      const res    = await fetch(dataUrl);
      const blob   = await res.blob();
      const fd     = new FormData();
      fd.append('file', blob, 'screenshot.png');
      const att = await uploadAttachment(fd).unwrap();
      setAttachments((prev) => [...prev.filter((a) => a.type !== 'screenshot'), att]);
    } catch { /* silencioso — el reporte igual se puede enviar */ }
    finally { setUploadingId(null); }
  }, [uploadAttachment]);

  // Sync pendingAttachment prop → local attachments state
  useEffect(() => {
    if (pendingAttachment) {
      setAttachments((prev) => [...prev.filter((a) => a.type !== 'video'), pendingAttachment]);
    }
  }, [pendingAttachment]);

  // ── Submit ─────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!description.trim()) return;
    // Fusionar pendingAttachment al enviar por si el useEffect aún no sincronizó
    // (race condition entre upload completo y click en Enviar reporte)
    const finalAttachments = pendingAttachment
      ? [...attachments.filter((a) => a.type !== pendingAttachment.type), pendingAttachment]
      : attachments;
    try {
      const { getAnalyticsSessionId } = await import('../../../lib/analytics');
      await createReport({
        kind,
        description: description.trim(),
        url:         window.location.href,
        userAgent:   navigator.userAgent,
        sessionId:   getAnalyticsSessionId(),
        // Una sugerencia también puede llevar captura o video: antes acá iba `[]` y el
        // adjunto se descartaba en silencio.
        attachments: finalAttachments,
        tags:        kind === 'suggestion' ? topics : undefined,
      }).unwrap();
      setDone(true);
    } catch { }
  };

  const removeAttachment = (type: BugAttachment['type']) =>
    setAttachments((prev) => prev.filter((a) => a.type !== type));

  const screenshot = attachments.find((a) => a.type === 'screenshot');
  const video      = attachments.find((a) => a.type === 'video');
  const busy       = submitting || !!uploadingId || !!attachmentUploading;
  const isSuggestion = kind === 'suggestion';

  // ── Annotation view ────────────────────────────────────────────
  if (view === 'annotate' && captureUrl) {
    return (
      <Dialog open={open} maxWidth="lg" fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', border: `1px solid ${alpha(golden, 0.2)}`, height: '90vh' } }}>
        <DialogTitle sx={{ pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={800}>Anotá el problema</Typography>
          <Tooltip title="Cancelar"><IconButton size="small" onClick={() => setView('form')}>
            <CloseIcon fontSize="small" />
          </IconButton></Tooltip>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AnnotationCanvas
            imageSrc={captureUrl}
            onExport={(url) => {
              setAnnotated(url);
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setView('form')} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => annotated && handleAnnotationConfirm(annotated)}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          >
            Usar captura
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Main form ──────────────────────────────────────────────────
  const cuerpo = (
    done ? (
          <Alert severity="success"
            sx={{ bgcolor: alpha('#34D399', 0.1), border: `1px solid ${alpha('#34D399', 0.3)}`, '& .MuiAlert-icon': { color: '#34D399' } }}>
            {isSuggestion
              ? '¡Gracias por tu idea! La registramos y el equipo la va a evaluar. Si la implementamos, la vas a ver en "Lo que pediste, lo hicimos".'
              : '¡Gracias! El equipo ya recibió tu reporte y lo vamos a revisar.'}
          </Alert>
        ) : (
          <Stack spacing={2}>
            {/* Selector con tap: elegir Problema o Mejora sin tener que scrollear el panel
                (los agentes no se daban cuenta de que se podían sugerir mejoras). */}
            {!soloSugerencia && <Stack direction="row" spacing={1}>
              {([
                { k: 'bug' as const,        label: 'Reportar problema', icon: <BugReportIcon sx={{ fontSize: 18 }} /> },
                { k: 'suggestion' as const, label: 'Sugerir mejora',     icon: <EmojiObjectsOutlinedIcon sx={{ fontSize: 18 }} /> },
              ]).map(({ k, label, icon }) => {
                const on = kind === k;
                return (
                  <Button
                    key={k}
                    onClick={() => setKind(k)}
                    startIcon={icon}
                    disabled={busy}
                    sx={{
                      flex: 1, textTransform: 'none', fontWeight: 700, fontSize: 13, py: 1, borderRadius: 1.5,
                      border: `1px solid ${on ? golden : alpha(P.text.primary, 0.12)}`,
                      bgcolor: on ? alpha(golden, 0.15) : 'transparent',
                      color: on ? golden : 'text.secondary',
                      '&:hover': { bgcolor: on ? alpha(golden, 0.2) : alpha(P.text.primary, 0.05), borderColor: on ? golden : alpha(P.text.primary, 0.25) },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Stack>}

            <Typography variant="body2" color="text.secondary" fontSize={13}>
              {isSuggestion
                ? 'Contanos tu idea y qué problema resolvería. Las mejores sugerencias las implementamos.'
                : 'Contanos qué pasó con el mayor detalle posible.'}
            </Typography>

            <TextField
              autoFocus multiline minRows={4} maxRows={8}
              placeholder={isSuggestion
                ? 'Ej: Estaría bueno ver un resumen del cliente al abrir su ficha, sin recorrer pestaña por pestaña.'
                : 'Ej: Al intentar guardar un cliente nuevo, el botón no responde y queda cargando.'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth disabled={busy}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.default', fontSize: 14,
                  '& fieldset':             { borderColor: alpha(golden, 0.2) },
                  '&:hover fieldset':       { borderColor: alpha(golden, 0.4) },
                  '&.Mui-focused fieldset': { borderColor: golden },
                },
              }}
            />

            {attachmentError && (
                <Alert
                  severity="warning"
                  sx={{ fontSize: 12, py: 0.5, bgcolor: alpha('#F59E0B', 0.1), border: `1px solid ${alpha('#F59E0B', 0.3)}`, '& .MuiAlert-icon': { color: '#F59E0B' } }}
                >
                  No pudimos subir la grabación. Podés enviar {isSuggestion ? 'la sugerencia' : 'el reporte'} sin ella o intentar grabar de nuevo.
                </Alert>
              )}

            {/**
              * Adjuntos: los mismos en bug y en sugerencia.
              *
              * La sugerencia no los ofrecía y el adjunto se descartaba al enviar. Evelyn
              * Muniz mandó el mismo planteo DOS veces —una como bug con captura y otra
              * como sugerencia sin captura— porque era la única forma de que la imagen
              * llegara: "es una sugerencia, pero en sugerencias no puedo agregar
              * imágenes". Las sugerencias con evidencia visual entraban por el canal de
              * bugs. (05/08/2026)
              */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {/* Captura */}
              {screenshot ? (
                <Chip
                  icon={<ImageIcon sx={{ fontSize: 14 }} />}
                  label="Captura adjunta"
                  size="small"
                  onDelete={() => removeAttachment('screenshot')}
                  sx={{ bgcolor: alpha('#34D399', 0.12), color: '#34D399', '& .MuiChip-deleteIcon': { color: '#34D399' } }}
                />
              ) : (
                <>
                  <Tooltip title="Capturá y anotá la pantalla actual">
                    <span>
                      <Button
                        size="small" variant="outlined"
                        startIcon={uploadingId === 'screenshot' ? <CircularProgress size={12} /> : <CameraAltIcon />}
                        disabled={busy}
                        onClick={handleCapture}
                        sx={{ textTransform: 'none', fontSize: 12, borderColor: alpha(golden, 0.3), color: golden,
                          '&:hover': { borderColor: golden, bgcolor: alpha(golden, 0.05) } }}
                      >
                        {uploadingId === 'screenshot' ? 'Subiendo…' : 'Adjuntar captura'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Subir una imagen desde tu equipo">
                    <Button
                      component="label" size="small" variant="text" disabled={busy}
                      startIcon={<ImageIcon sx={{ fontSize: 14 }} />}
                      sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
                    >
                      Subir imagen
                      <input hidden type="file" accept="image/*" onChange={(e) => handleManualImage(e.target.files?.[0])} />
                    </Button>
                  </Tooltip>
                </>
              )}

              {/* Video */}
              {video ? (
                <Chip
                  icon={<VideocamIcon sx={{ fontSize: 14 }} />}
                  label="Video adjunto"
                  size="small"
                  onDelete={() => removeAttachment('video')}
                  sx={{ bgcolor: alpha('#60A5FA', 0.12), color: '#60A5FA', '& .MuiChip-deleteIcon': { color: '#60A5FA' } }}
                />
              ) : (
                <Tooltip title={recording.phase === 'recording' ? 'Grabación en curso' : 'Grabá tu pantalla y navegá el CRM libremente'}>
                  <span>
                    <Button
                      size="small" variant="outlined"
                      startIcon={attachmentUploading ? <CircularProgress size={12} /> : <VideocamIcon />}
                      disabled={busy || recording.phase !== 'idle'}
                      onClick={() => { onClose(); recording.startRecording(); }}
                      sx={{ textTransform: 'none', fontSize: 12, borderColor: alpha('#60A5FA', 0.3), color: '#60A5FA',
                        '&:hover': { borderColor: '#60A5FA', bgcolor: alpha('#60A5FA', 0.05) } }}
                    >
                      {attachmentUploading ? 'Subiendo…' : 'Grabar pantalla'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>

            {captureError && (
              <Typography variant="caption" sx={{ color: '#f87171', display: 'block', mt: 0.5 }}>
                No se pudo capturar la pantalla automáticamente. Usá "Subir imagen" para adjuntar una captura desde tu equipo.
              </Typography>
            )}

            {/* Thumbnail preview */}
            {screenshot && (
              <Box sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
                <img src={screenshot.url} alt="captura" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} />
                <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                  <Tooltip title="Sacar nueva captura">
                    <IconButton size="small" onClick={handleCapture} disabled={busy}
                      sx={{ bgcolor: alpha('#000', 0.6), color: '#fff', '&:hover': { bgcolor: alpha('#000', 0.8) } }}>
                      <CameraAltIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* Cierra el bloque de adjuntos, en la misma posición que en modo bug: dice
                desde qué pantalla lo mandaron, que es la mitad del contexto. */}
            <Typography variant="caption" color="text.disabled" fontSize={11}>
              URL actual: <span style={{ color: golden }}>{window.location.pathname}</span>
            </Typography>

            {/* Modo sugerencia: clasificá por área (filtro de temas). Va al final: primero
                mostrás lo que viste, después lo clasificás. */}
            {isSuggestion && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                  ¿Sobre qué área? <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>(opcional)</Box>
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {SUGGESTION_TOPICS.map((t) => {
                    const on = topics.includes(t);
                    return (
                      <Chip
                        key={t}
                        label={t}
                        size="small"
                        onClick={() => setTopics((prev) => on ? prev.filter((x) => x !== t) : [...prev, t])}
                        sx={{
                          cursor: 'pointer', fontWeight: 700, fontSize: 12,
                          bgcolor: on ? alpha(golden, 0.18) : alpha(P.text.primary, 0.04),
                          color: on ? golden : 'text.secondary',
                          border: `1px solid ${on ? alpha(golden, 0.5) : alpha(P.text.primary, 0.12)}`,
                          '&:hover': { bgcolor: on ? alpha(golden, 0.24) : alpha(P.text.primary, 0.08) },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            )}

            {error && <Alert severity="error">No pudimos enviar el reporte. Intentá de nuevo.</Alert>}
          </Stack>
        )
  );

  const acciones = (
    done ? (
          <Button onClick={handleClose} variant="contained"
            sx={{ fontWeight: 700, textTransform: 'none' }}>
            Cerrar
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={busy} sx={{ color: 'text.secondary', textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!description.trim() || busy}
              variant="contained"
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              {submitting ? 'Enviando…' : (isSuggestion ? 'Enviar sugerencia' : 'Enviar reporte')}
            </Button>
          </>
        )
  );

  // Modo inline: el reporte vive DENTRO del panel de Gustavo y entra deslizándose desde
  // la derecha, en vez de abrirse como un diálogo encima. Mismo formulario, otro marco.
  if (inline) {
    return (
      <Box sx={{ height: '100%', width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* overflowX oculto: el formulario tiene filas anchas (adjuntos, selector) que
            desbordaban el panel y empujaban el carril fuera de lugar. */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 2.5, py: 2, minWidth: 0, minHeight: 0 }}>
          {cuerpo}
        </Box>
        <Box sx={{
          px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1,
          borderTop: `1px solid ${alpha(golden, 0.12)}`,
        }}>
          {acciones}
        </Box>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-bug-dialog
      PaperProps={{ sx: { bgcolor: 'background.paper', border: `1px solid ${alpha(golden, 0.2)}` } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}>
        {isSuggestion ? <EmojiObjectsOutlinedIcon sx={{ color: golden }} /> : <BugReportIcon sx={{ color: golden }} />}
        <Typography component="span" fontWeight={800} fontSize={18}>
          {isSuggestion ? 'Sugerir una mejora' : 'Reportar un problema'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>{cuerpo}</DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>{acciones}</DialogActions>
    </Dialog>
  );
}
